import { Children } from 'react';
import parseISO from 'date-fns/parseISO';

import withStyles from '@mui/styles/withStyles';

import Typography from '@classify/react-core-components/Typography';
import LearningProgress from '@classify/react-product-components/LearningProgress';
import { Card } from '../../../cards';
import {
  LearningContentListItem,
  learningContentDefaultTranslations,
} from '../../../learning-content';

import Illustration from '../Illustration/Illustration';

const styles = /* istanbul ignore next */ ({ spacing, breakpoints, palette }) => ({
  endDateContainer: {
    borderTop: `1px solid ${palette.layout.line.divider}`,
    textAlign: 'left',
    [breakpoints.up('md')]: {
      height: '108px',
      width: '130px',
      margin: 'auto',
      borderTop: 'none',
      borderLeft: `1px solid ${palette.layout.line.divider}`,
      textAlign: 'center',
    },
  },
  endDateContent: {
    margin: spacing(2),
    padding: spacing(1),
    [breakpoints.up('sm')]: {
      margin: 0,
    },
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    [breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  childrenRowContainer: {
    borderTop: `1px solid ${palette.layout.line.divider}`,
  },
  illustration: {
    paddingBottom: '68%',
    [breakpoints.down('sm')]: {
      paddingBottom: 0,
      height: 170,
    },
  },
  illustrationContainer: {
    width: 205,
    [breakpoints.down('sm')]: {
      maxWidth: 'none',
      width: '100%',
    },
  },

  noWrap: {
    whiteSpace: 'nowrap',
  },
  cardContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  informationContainer: {},
  root: {
    flex: '1',
  },
  cardRoot: {
    boxShadow: 'none',
    border: `1px solid ${palette.layout.line.divider}`,
  },
  description: {
    display: 'block',
  },
  listItemInner: {
    padding: 0,
    alignItems: 'center',
    '& $informationContainer': {
      width: '92%',
      color: palette.text.onMain,
      margin: spacing(0, 2),
      [breakpoints.down('sm')]: {
        margin: spacing(2),
      },
    },
    [breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
});

export const PathItemWithUserInformation = ({
  categoryColor,
  children = null,
  classes = {},
  className = '',
  endDate = null,
  openedAt = null,
  progress = null,
  expectedProgress = null,
  title,
  titleProps = {},
  isProgressActive = false,
  locale = 'en',

  translations = {
    endDate: 'Funding ends: ',
    partners: 'Partners',
    ...learningContentDefaultTranslations,
    ariaLabel: 'Show more information',
    progressBarAriaLabel: '0% achieved',
  },
  ...otherProps
}) => {
  let stringifiedDate;
  /* istanbul ignore next */
  if (endDate) {
    const dateOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    stringifiedDate = parseISO(endDate).toLocaleDateString(locale, dateOptions);
  }

  return (
    <Card
      classes={{ root: classes.cardRoot, content: classes.cardContent }}
      className={className}
      ariaLabel={translations.ariaLabel}
    >
      <div className={classes.cardContainer}>
        <LearningContentListItem
          illustrationComponent={Illustration}
          title={title}
          className={classes.root}
          classes={{
            illustrationContainer: classes.illustrationContainer,
            illustration: classes.illustration,
            informationContainer: classes.informationContainer,
            listItemInner: classes.listItemInner,
          }}
          categoryColor={categoryColor}
          shouldHandleHover={false}
          openedAt={openedAt}
          titleProps={{
            variant: 'h4',
            component: 'div',
            ...titleProps,
          }}
          locale={locale}
          translations={translations}
          {...otherProps}
        >
          {progress !== null && (
            <LearningProgress
              value={progress}
              expectedValue={expectedProgress}
              maxValue={1}
              variant="path"
              isDisabled={!isProgressActive}
              aria-label={translations.progressBarAriaLabel}
            />
          )}
        </LearningContentListItem>
        {stringifiedDate && (
          <div className={classes.endDateContainer}>
            <Typography className={classes.endDateContent} component="div" variant="body">
              {translations.endDate}
              <br />
              <strong>{stringifiedDate}</strong>
            </Typography>
          </div>
        )}
      </div>
      {Children.map(children, (child) => (
        <div className={classes.childrenRowContainer}>{child}</div>
      ))}
    </Card>
  );
};

export default withStyles(styles)(PathItemWithUserInformation);
